import React from 'react';
import { renderToString } from 'react-dom/server';
import { keys, get } from 'lodash-es';

import columnChart from '../../../../../components/Chart/ColumnChart';
import { format } from '../../../../../utils/date';
import { numberToHumanChart, numberToPercentage } from '../../../../../utils/formatter';

export const columnChartConfig = ({
  series,
  type = 'column',
  unit = '%',
  tooltipPrecision = 1,
  yAxisPrecision,
}) => {
  const isPercentage = unit === '%';
  const firstSeriesKey = keys(series)[0];
  const firstSeries = get(series, [firstSeriesKey], []);

  const xTickPositions = firstSeries?.map((value) => value[0]) || [];

  return columnChart({
    chartOptions: {
      spacing: [10, 0, 0, 0],
      height: 234,
      type,
    },
    yAxis: {
      title: null,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          if (isPercentage) {
            return `${this.value}%`;
          }
          return numberToHumanChart(
            this.value,
            yAxisPrecision ? { precision: yAxisPrecision } : undefined
          );
        },
      },
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
    },
    xAxis: {
      tickLength: 0,
      lineWidth: 1,
      lineColor: '#EDF0F5',
      maxPadding: type === 'column' ? 0.02 : 0.05,
      crosshair: {
        color: '#E1E5EB',
        width: 1,
      },
      labels: {
        formatter() {
          if (this.isFirst || this.isLast) {
            return `<span class="text-nowrap">${format(this.value, 'MMM ‘YY')}</span>`;
          }
          return '';
        },
        style: {
          color: '#848EA1',
          fontSize: 12,
          textOverflow: 'none',
          width: 40,
        },
        useHTML: true,
      },
      type: 'datetime',
      tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
      showFirstLabel: true,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        if (!Array.isArray(this.points)) return false;

        const pointOptions = this.points[0].point.options;
        const isPartialElem = pointOptions.isPartial;

        const renderTooltipContent = (rowsContent) =>
          renderToString(
            <div
              style={{
                minWidth: '170px',
                borderRadius: '8px',
                border: '1px solid #E1E5EB',
                padding: '12px 16px',
                backgroundColor: '#fff',
                boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
              }}
            >
              <table className="table">
                <tr>
                  <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                    {format(this.x, 'MMM`YY')} {isPartialElem && '(Partial Period)'}
                  </td>
                </tr>
                {rowsContent}
              </table>
            </div>
          );

        return renderTooltipContent(
          this.points.map((point) => (
            <tr key={point.series.name}>
              <td className="text-left text-black text-14 p-l-0 p-b-0 pr-md">
                <div className="text-truncate text-black text-14" style={{ maxWidth: '100%' }}>
                  <span
                    className="tooltip-bubble-marker"
                    style={{ backgroundColor: point.series.color, borderColor: point.series.color }}
                  />
                  <span>{point.series.name}</span>
                </div>
              </td>
              <td
                className="text-right text-bold text-black text-14 p-l-0 p-b-0"
                style={{ lineHeight: '150%' }}
              >
                <span>
                  {isPercentage
                    ? numberToPercentage(point.y, { precision: tooltipPrecision })
                    : numberToHumanChart(point.y, { precision: tooltipPrecision })}
                </span>
              </td>
            </tr>
          ))
        );
      },
    },
    plotOptions: {
      column: {
        minPointLength: 5,
        borderRadius: 0,
        borderWidth: 0,
        groupPadding: 0.1,
      },
    },
  });
};
